<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="tournament-deck-title"
    @cancel.prevent="onCancel"
    @click="onScrimClick"
  >
    <div class="panel">
      <h2 id="tournament-deck-title">{{ t('tournament.pickTitle') }}</h2>
      <p>{{ t('tournament.pickBody') }}</p>
      <div class="list" role="listbox" :aria-label="t('tournament.pickTitle')">
        <button
          v-for="deck in options"
          :key="deck.id"
          type="button"
          class="deck"
          role="option"
          :aria-selected="deck.id === selected"
          :class="{ on: deck.id === selected }"
          :disabled="tournament.busy"
          @click="selected = deck.id"
        >
          <span class="name">{{ deck.name }}</span>
          <span v-if="deck.id === activeId" class="mark">{{ t('deck.inDuels') }}</span>
        </button>
      </div>
      <p v-if="errorText" class="error">{{ errorText }}</p>
      <div class="actions">
        <button
          type="button"
          class="md-btn-tonal"
          :disabled="tournament.busy || !selected"
          @click="confirm"
        >
          {{ t('tournament.pickConfirm') }}
        </button>
        <button type="button" class="text-btn" :disabled="tournament.busy" @click="onCancel">
          {{ t('tournament.callLater') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { TOURNAMENT_JOIN_CUTOFF_MS } from '@shared/index'
import { listDeckPlayOptions } from '../game/localDeck'
import { useI18n } from '../i18n/t'
import { useDeckStore } from '../stores/deck'
import { useTournamentStore } from '../stores/tournament'

const { t } = useI18n()
const tournament = useTournamentStore()
const deckStore = useDeckStore()
const dialogEl = ref<HTMLDialogElement | null>(null)
const options = listDeckPlayOptions().filter((row) => row.playable)
const activeId = deckStore.activeId
const selected = ref(
  options.find((row) => row.id === activeId)?.id ?? options[0]?.id ?? '',
)
let sent = false
let autoSent = false
let timer: ReturnType<typeof setInterval> | null = null

const errorText = computed(() => {
  if (!tournament.error) return ''
  const key = `tournament.errors.${tournament.error}`
  const text = t(key)
  return text === key ? t('tournament.errors.deck') : text
})

function onCancel(): void {
  if (tournament.busy || sent) return
  tournament.closePicker()
}

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) onCancel()
}

async function confirm(): Promise<void> {
  if (sent || tournament.busy || !selected.value) return
  sent = true
  await tournament.join(selected.value)
  if (tournament.error) {
    sent = false
    return
  }
  tournament.closePicker()
}

async function useActive(): Promise<void> {
  if (sent || tournament.busy) return
  sent = true
  await tournament.joinActive()
  if (tournament.error) {
    sent = false
    return
  }
  tournament.closePicker()
}

function tick(): void {
  if (autoSent || sent || tournament.busy) return
  const start = Date.parse(tournament.current?.startsAt ?? '')
  if (!Number.isFinite(start)) return
  if (start - Date.now() > TOURNAMENT_JOIN_CUTOFF_MS) return
  autoSent = true
  void useActive()
}

onMounted(() => {
  dialogEl.value?.showModal()
  timer = setInterval(tick, 250)
  tick()
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  overflow: auto;
}

.scrim::backdrop {
  background: var(--md-scrim);
}

.panel {
  width: min(420px, calc(100vw - 32px));
  padding: 24px;
  border-radius: 28px;
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
}

h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.3;
}

p {
  margin: 12px 0 0;
  color: var(--md-sys-color-on-surface-variant);
}

.list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: min(320px, 46vh);
  margin-top: 16px;
  overflow: auto;
}

.deck {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 8px 14px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 12px;
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 15px;
  text-align: left;
  cursor: pointer;
}

.deck.on {
  border-color: var(--md-sys-color-primary);
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.deck:disabled {
  cursor: default;
}

.name {
  min-width: 0;
}

.mark {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.error {
  color: var(--md-sys-color-error);
}

.actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
}

.md-btn-tonal,
.text-btn {
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.md-btn-tonal {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.md-btn-tonal:disabled,
.text-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.text-btn {
  background: transparent;
  color: var(--md-sys-color-primary);
}
</style>
